import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { Button } from '@openng/optimus-ui/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { LegacyButtonStyleService } from '@app/core/services/legacy-button-style/legacy-button-style.service';

/**
 * @deprecated Do not add new instances of this component. Use Optimus UI's 'p-accordion' instead.
 */
@Component({
  selector: 'ag1-accordion',
  templateUrl: './ag1-accordion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Button, AngularSvgIconModule],
})
export class Ag1AccordionComponent {
  private _legacyButtonStyleService = inject(LegacyButtonStyleService);

  /**
   * (Required) - the main/left label of the accordion
   */
  readonly label = input<string>(undefined);

  /**
   * (Optional) - used to specify the path of the icon, eg. '/assets/icons/general/plus.svg'
   */
  readonly labelHighlightIconPath = input<string>(undefined);

  /**
   * (Optional) - used to give a colour to the icon defined above, eg. 'red'
   */
  readonly labelHighlightIconColor = input<string>('french-blue');

  /**
   * (Optional) - used to conditionally show accordion sequences.
   */
  readonly showSequence = input<boolean>(false);

  /**
   * (Optional) - In cases of multiple accordions, used to sequence accordions
   */
  readonly sequence = input<number>(undefined);

  /**
   * (Optional) - used to conditionally show accordion content status,ie. is it shown or hidden.
   * Parent binds one-way; toggle writes a local copy and notifies via accordionExpandEvent.
   */
  readonly isVisible = input(false);
  readonly localIsVisible = linkedSignal(() => this.isVisible());

  /**
   * (Optional) - used to specify the variant of the accordion, eg. 'primary', 'secondary', etc.
   */
  readonly variant = input<string>('secondary');

  readonly buttonStyle = computed(() =>
    this._legacyButtonStyleService.toOptimusButtonStyle(this.variant()),
  );

  /**
   * (Optional) - used to conditionally show the 'show/hide' label
   */
  readonly addShowLabel = input<boolean>(true);

  /**
   * (Optional) - used to specify the class overrides of the accordion
   */
  readonly classOverrides = input<string>(undefined);

  /**
   * (Optional) - used to specify the right label of the accordion
   */
  readonly rightLabel = input<string>('');

  /**
   * (Optional) - used to conditionally show the right label of the accordion
   */
  readonly showRightLabel = input<boolean>(false);

  /**
   * (Optional) - used to specify the class overrides of the accordion text
   */
  readonly textClasses = input<string>('');

  /**
   * (Optional) - used to emit the accordion expand event, eg. when the accordion is expanded or collapsed
   */
  readonly accordionExpandEvent = output<boolean>();

  toggleAccordion() {
    this.localIsVisible.update((visible) => !visible);
    this.accordionExpandEvent.emit(this.localIsVisible());
  }
}
